import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import dayjs from 'dayjs';
import { BulkDataStructureRequest, BulkDataStructureResponse } from 'src/app/models/bulk_data_structure.model';
import { CompanyProductUserMember } from 'src/app/models/company_product_user_member.model';
import { CompanyProductUserMemberRole } from 'src/app/models/company_product_user_member_role.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services';
import { UtilsService } from 'src/app/services/utils.service';
import { SelectorDialogComponent } from 'src/app/components/main/shared/selector-dialog/selector-dialog.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { UserProfilePictureComponent } from 'src/app/components/utils/user-profile-picture/user-profile-picture.component';

@Component({
  selector: 'app-company-product-members-dialog',
  templateUrl: './company-product-members-dialog.component.html',
  styleUrl: './company-product-members-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    MatOption,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatIconButton,
    MatTooltip,
    MatIcon,
    MatDialogActions,
    MatButton,
    UserProfilePictureComponent,
  ],
})
export class CompanyProductMembersDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<CompanyProductMembersDialogComponent>);
  readonly data = inject<{ company_product_id: number; company_product_title: string }>(MAT_DIALOG_DATA);
  private readonly api = inject(ApiService);
  private readonly utils = inject(UtilsService);
  protected readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);

  workers: User[] | undefined;
  partners: User[] | undefined;
  company_product_user_member_roles: CompanyProductUserMemberRole[] = [];
  form: FormGroup;
  customPortionModeOverrides: Record<string, 'input' | 'select'> = {};
  portionDisplayModes: Record<string, ('input' | 'select')[]> = {};
  defaultExpectedProductionPrcntPlaceholderByUserId: Record<string, string> = {};
  memberRows = signal<{ userId: string; user: User; members: FormGroup[] }[]>([]);

  private readonly presetPortionValues = [0, 0.25, 0.5, 0.75, 1];
  private readonly initial_members_by_id = new Map<number, MemberRowSnapshot>();
  private readonly pending_delete_ids = new Set<number>();

  /** Mondays from the current ISO week onwards (past weeks are locked in the form). */
  datepickerFilterStartDate = (d: Date | null): boolean => {
    if (d == null) {
      return false;
    }
    const day = dayjs(d);
    return day.day() === 1 && day.isSameOrAfter(dayjs().startOf('week'), 'day');
  };

  /** Sundays from the current ISO week onwards. */
  datepickerFilterEndDate = (d: Date | null): boolean => {
    if (d == null) {
      return false;
    }
    const day = dayjs(d);
    return day.day() === 0 && day.isSameOrAfter(dayjs().startOf('week').endOf('week'), 'day');
  };

  ngOnInit(): void {
    this.fetchMembers();
    this.fetchRoles();
    this.fetchActiveWorkers();
    this.fetchPartnerUsers();
  }

  addUser() {
    const dialogRef = this.dialog.open(SelectorDialogComponent, {
      width: '400px',
      data: {
        dialog_title: 'Selecciona un miembro',
        dialog_ok_button: 'Seleccionar',
        dialog_cancel_button: 'Cancelar',
        data: this.workers!.filter(user => !this.form.contains(user.id.toString()))
          .concat(this.partners!)
          .map(user => {
            return {
              value: user.id,
              textToShow: user.name + ' ' + user.surnames,
            };
          }),
      },
    });

    dialogRef.afterClosed().subscribe((user_id: number | undefined) => {
      if (user_id) {
        const user = this.workers!.concat(this.partners!).find(u => u.id == user_id) as User;
        this.rememberDefaultExpectedProductionPrcnt(user_id, user.default_expected_production_prcnt);
        if (!this.form.contains(user_id.toString())) {
          this.form.addControl(
            user_id.toString(),
            this.fb.group({
              user: this.fb.control(user),
              company_product_user_members: this.fb.array([]),
            })
          );
        }

        const group: FormGroup = this.createGroupRowForUser(null, null, null, dayjs().startOf('week').toDate(), null, null);
        group.markAllAsTouched();
        this.getCompanyProductUserMembersArray(user_id)?.push(group);
        this.rebuildPortionDisplayModes();
        this.rebuildMemberRows();
      }
    });
  }

  close(result?: CompanyProductMembersDialogResult): void {
    this.dialogRef.close(result);
  }

  addEndDate(formGroup: FormGroup) {
    formGroup.setControl('end_date', this.fb.control(dayjs().endOf('week').toDate(), Validators.compose([Validators.required])));
    formGroup.markAllAsTouched();
  }

  clearEndDate(formGroup: FormGroup) {
    formGroup.controls['end_date'].patchValue(null);
    formGroup.markAllAsTouched();
  }

  canRemoveMemberRow(formGroup: FormGroup): boolean {
    const id = formGroup.getRawValue().id as number | null;
    if (id == null) {
      return true;
    }
    const start = dayjs(formGroup.getRawValue().start_date).startOf('week');
    return start.isSameOrAfter(dayjs().startOf('week'), 'day');
  }

  removeMemberRow(user_id: number | string, row_index: number): void {
    const members = this.getCompanyProductUserMembersArray(user_id);
    if (members == null || row_index < 0 || row_index >= members.length) {
      return;
    }
    const formGroup = members.at(row_index) as FormGroup;
    if (!this.canRemoveMemberRow(formGroup)) {
      return;
    }
    const id = formGroup.getRawValue().id as number | null;
    if (id != null) {
      this.pending_delete_ids.add(id);
      this.initial_members_by_id.delete(id);
    }
    members.removeAt(row_index);
    if (members.length === 0) {
      this.form.removeControl(String(user_id));
    }
    this.rebuildPortionDisplayModes();
    this.rebuildMemberRows();
  }

  isPresetPortionValue(value: number | null | undefined): boolean {
    if (value == null) {
      return false;
    }
    const numeric = Number(value);
    return this.presetPortionValues.some(v => Math.abs(v - numeric) < 0.000001);
  }

  toggleCustomPortionMode(userId: string | number, rowIndex: number, formGroup: FormGroup): void {
    const userKey = String(userId);
    const key = this.portionKey(userId, rowIndex);
    const currentMode = this.portionDisplayModes[userKey]?.[rowIndex] ?? this.resolvePortionDisplayMode(userId, rowIndex, formGroup);
    const nextMode: 'input' | 'select' = currentMode === 'input' ? 'select' : 'input';
    this.customPortionModeOverrides[key] = nextMode;
    const rowModes = [...(this.portionDisplayModes[userKey] ?? [])];
    rowModes[rowIndex] = nextMode;
    this.portionDisplayModes = { ...this.portionDisplayModes, [userKey]: rowModes };
  }

  clampPortionValue(formGroup: FormGroup): void {
    const control = formGroup.get('portion_of_productive_work_day');
    if (control == null || control.disabled) {
      return;
    }
    const raw = Number(control.value);
    if (Number.isNaN(raw)) {
      return;
    }
    const clamped = Math.max(0, Math.min(1, raw));
    if (clamped !== raw) {
      control.patchValue(clamped);
    }
    control.markAsTouched();
  }

  addChangeOfRole(user_id: number, formGroup: FormGroup) {
    if (!formGroup.controls['end_date'].disabled && formGroup.value['end_date'] != null) {
      this.addEndDate(formGroup);
    }
    const newGroupRow: FormGroup = this.createGroupRowForUser(null, null, null, dayjs().startOf('week').toDate(), null, null);
    newGroupRow.markAllAsTouched();
    this.getCompanyProductUserMembersArray(user_id)?.push(newGroupRow);
    this.rebuildPortionDisplayModes();
    this.rebuildMemberRows();
  }

  submit() {
    const users_by_id: Record<number, User> = {};
    Object.keys(this.form.controls).forEach(user_id => {
      const user = (this.form.get(user_id) as FormGroup)?.get('user')?.value as User | undefined;
      if (user != null) {
        users_by_id[Number(user_id)] = user;
      }
    });

    const body = this.prepareBulkPayload();
    this.disableFormAfterSubmit();

    if (body.create_or_update.length > 0 || body.delete.length > 0) {
      this.api.bulkMembers(this.data.company_product_id, body).subscribe(bulk => {
        this.close({
          saved: true,
          company_product_id: this.data.company_product_id,
          bulk,
          users_by_id,
        });
      });
    } else {
      this.close();
    }
  }

  private fetchRoles() {
    this.api.getCompanyProductUserMemberRoles().subscribe(data => {
      this.company_product_user_member_roles = data.data;
    });
  }

  private fetchMembers() {
    this.api
      .getBisualProductionTeamNowAndFuture(this.data.company_product_id.toString(), {
        with_default_expected_production_prcnt: true,
      })
      .subscribe(data => {
        this.mountForm(data);
      });
  }

  private mountForm(data: CompanyProductUserMember[]) {
    this.initial_members_by_id.clear();
    this.pending_delete_ids.clear();
    const formData: Record<string, FormGroup> = {};
    data.forEach(company_product_user_member => {
      this.rememberDefaultExpectedProductionPrcnt(
        company_product_user_member.user.id,
        company_product_user_member.default_expected_production_prcnt
      );
      if (formData[company_product_user_member.user.id] == null) {
        formData[company_product_user_member.user.id] = this.fb.group({
          user: this.fb.control(company_product_user_member.user),
          company_product_user_members: this.fb.array([]),
        });
      }

      this.initial_members_by_id.set(company_product_user_member.id, this.snapshotFromMember(company_product_user_member));

      const group: FormGroup = this.createGroupRowForUser(
        company_product_user_member.company_product_user_member_role_id,
        company_product_user_member.portion_of_productive_work_day,
        company_product_user_member.expected_production_prcnt,
        company_product_user_member.start_date,
        company_product_user_member.end_date,
        company_product_user_member.id
      );
      (formData[company_product_user_member.user.id].get('company_product_user_members') as FormArray).push(group);
    });

    this.form = this.fb.group(formData);
    this.rebuildPortionDisplayModes();
    this.rebuildMemberRows();
  }

  private createGroupRowForUser(
    company_product_user_member_role_id: number | null,
    portion_of_productive_work_day: number | null,
    expected_production_prcnt: number | null,
    start_date: Date,
    end_date: Date | null,
    company_product_user_member_id: number | null
  ) {
    const group = this.fb.group({
      id: [company_product_user_member_id],
      company_product_user_member_role_id: [company_product_user_member_role_id, Validators.compose([Validators.required])],
      portion_of_productive_work_day: [
        portion_of_productive_work_day,
        Validators.compose([Validators.required, Validators.min(0), Validators.max(1)]),
      ],
      expected_production_prcnt: [expected_production_prcnt, Validators.compose([Validators.min(0), Validators.max(100)])],
      start_date: [start_date, Validators.compose([Validators.required])],
      end_date: [end_date, Validators.compose([])],
    });

    if (this.isMemberRowStartLocked(start_date)) {
      group.controls['start_date'].disable();
      group.controls['company_product_user_member_role_id'].disable();
      group.controls['portion_of_productive_work_day'].disable();
      group.controls['expected_production_prcnt'].disable();
    }
    if (end_date != null && this.isMemberRowEndLocked(end_date)) {
      group.controls['end_date'].disable();
    }

    return group;
  }

  /** Past weeks are read-only; current week (and future) stays editable. */
  private isMemberRowStartLocked(start_date: Date): boolean {
    return dayjs(start_date).startOf('week').isBefore(dayjs().startOf('week'), 'day');
  }

  private isMemberRowEndLocked(end_date: Date): boolean {
    return dayjs(end_date).isBefore(dayjs().startOf('week'), 'day');
  }

  private prepareBulkPayload(): BulkDataStructureRequest<CompanyProductUserMember> {
    const body: BulkDataStructureRequest<CompanyProductUserMember> = {
      create_or_update: [],
      delete: [...this.pending_delete_ids],
    };

    Object.keys(this.form.controls).forEach(user_id => {
      const members = this.getCompanyProductUserMembersArray(user_id);
      members?.controls.forEach((control: AbstractControl) => {
        this.appendFormRowToBulkPayload(body, +user_id, control as FormGroup);
      });
    });

    body.delete = [...new Set(body.delete)];
    return body;
  }

  private appendFormRowToBulkPayload(
    body: BulkDataStructureRequest<CompanyProductUserMember>,
    user_id: number,
    formGroup: FormGroup
  ): void {
    this.clampPortionValue(formGroup);
    const raw = formGroup.getRawValue();
    const snapshot = this.snapshotFromRaw(raw);
    const existing_id = raw.id as number | null;

    if (existing_id == null) {
      body.create_or_update.push(this.buildCreatePayload(user_id, raw));
      return;
    }

    const initial = this.initial_members_by_id.get(existing_id);
    if (initial == null) {
      body.create_or_update.push(this.buildCreatePayload(user_id, raw));
      return;
    }

    if (this.snapshotsEqual(initial, snapshot)) {
      return;
    }

    if (this.isOnlyEndDateShortened(initial, snapshot)) {
      body.create_or_update.push({
        id: existing_id,
        end_date: snapshot.end_date_api,
      } as unknown as CompanyProductUserMember);
      return;
    }

    body.delete.push(existing_id);
    body.create_or_update.push(this.buildCreatePayload(user_id, raw));
  }

  private buildCreatePayload(user_id: number, raw: Record<string, unknown>): CompanyProductUserMember {
    const start_date = this.utils.normalizeMadridDateForApi(raw['start_date'] as Date, 'start') as unknown as Date;
    const end_raw = raw['end_date'] as Date | null;
    return {
      user_id,
      company_product_id: this.data.company_product_id,
      company_product_user_member_role_id: Number(raw['company_product_user_member_role_id']),
      portion_of_productive_work_day: Number(raw['portion_of_productive_work_day']),
      expected_production_prcnt: raw['expected_production_prcnt'] == null ? null : Number(raw['expected_production_prcnt']),
      start_date,
      end_date: end_raw != null ? (this.utils.normalizeMadridDateForApi(end_raw, 'end') as unknown as Date) : null,
    } as CompanyProductUserMember;
  }

  private snapshotFromMember(member: CompanyProductUserMember): MemberRowSnapshot {
    return {
      company_product_user_member_role_id: Number(member.company_product_user_member_role_id),
      portion_of_productive_work_day: Number(member.portion_of_productive_work_day),
      expected_production_prcnt: member.expected_production_prcnt == null ? null : Number(member.expected_production_prcnt),
      start_date_key: dayjs(member.start_date).format('YYYY-MM-DD'),
      end_date_key: member.end_date != null ? dayjs(member.end_date).format('YYYY-MM-DD') : null,
      end_date_api:
        member.end_date != null ? (this.utils.normalizeMadridDateForApi(member.end_date as Date, 'end') as unknown as Date) : null,
    };
  }

  private snapshotFromRaw(raw: Record<string, unknown>): MemberRowSnapshot {
    const end_raw = raw['end_date'] as Date | null;
    return {
      company_product_user_member_role_id: Number(raw['company_product_user_member_role_id']),
      portion_of_productive_work_day: Number(raw['portion_of_productive_work_day']),
      expected_production_prcnt:
        raw['expected_production_prcnt'] == null || raw['expected_production_prcnt'] === ''
          ? null
          : Number(raw['expected_production_prcnt']),
      start_date_key: dayjs(raw['start_date'] as Date).format('YYYY-MM-DD'),
      end_date_key: end_raw != null ? dayjs(end_raw).format('YYYY-MM-DD') : null,
      end_date_api: end_raw != null ? (this.utils.normalizeMadridDateForApi(end_raw, 'end') as unknown as Date) : null,
    };
  }

  private snapshotsEqual(a: MemberRowSnapshot, b: MemberRowSnapshot): boolean {
    return (
      a.company_product_user_member_role_id === b.company_product_user_member_role_id &&
      Math.abs(a.portion_of_productive_work_day - b.portion_of_productive_work_day) < 0.000001 &&
      a.expected_production_prcnt === b.expected_production_prcnt &&
      a.start_date_key === b.start_date_key &&
      a.end_date_key === b.end_date_key
    );
  }

  private isOnlyEndDateShortened(initial: MemberRowSnapshot, current: MemberRowSnapshot): boolean {
    const same_immutable =
      initial.company_product_user_member_role_id === current.company_product_user_member_role_id &&
      Math.abs(initial.portion_of_productive_work_day - current.portion_of_productive_work_day) < 0.000001 &&
      initial.expected_production_prcnt === current.expected_production_prcnt &&
      initial.start_date_key === current.start_date_key;
    if (!same_immutable) {
      return false;
    }
    if (initial.end_date_key === current.end_date_key) {
      return false;
    }
    if (current.end_date_key == null) {
      return false;
    }
    if (initial.end_date_key == null) {
      return true;
    }
    return dayjs(current.end_date_key).isBefore(dayjs(initial.end_date_key), 'day');
  }

  private disableFormAfterSubmit(): void {
    Object.keys(this.form.controls).forEach(user_id => {
      const members = this.getCompanyProductUserMembersArray(user_id);
      members?.controls.forEach(control => (control as FormGroup).disable());
    });
    this.form.disable();
  }

  private getCompanyProductUserMembersArray(userId: string | number): FormArray | null {
    return this.form.get(String(userId))?.get('company_product_user_members') as FormArray | null;
  }

  private fetchActiveWorkers() {
    this.api.getActiveEmployees({ with_default_expected_production_prcnt: true }).subscribe(data => {
      this.workers = data;
      data.forEach(user => {
        this.rememberDefaultExpectedProductionPrcnt(user.id, user.default_expected_production_prcnt);
      });
    });
  }

  private rememberDefaultExpectedProductionPrcnt(userId: string | number, default_prcnt: number | null | undefined): void {
    if (default_prcnt == null) {
      return;
    }
    this.defaultExpectedProductionPrcntPlaceholderByUserId = {
      ...this.defaultExpectedProductionPrcntPlaceholderByUserId,
      [String(userId)]: String(default_prcnt),
    };
  }

  private fetchPartnerUsers() {
    this.api.getUsers({ 'role-slug': 'partner' }).subscribe(data => {
      this.partners = data.data;
    });
  }

  private portionKey(userId: string | number, rowIndex: number): string {
    return `${userId}_${rowIndex}`;
  }

  private resolvePortionDisplayMode(userId: string | number, rowIndex: number, formGroup: FormGroup): 'input' | 'select' {
    const key = this.portionKey(userId, rowIndex);
    const override = this.customPortionModeOverrides[key];
    if (override != null) {
      return override;
    }
    const value = formGroup.get('portion_of_productive_work_day')?.value;
    return this.isPresetPortionValue(value) ? 'select' : 'input';
  }

  private rebuildPortionDisplayModes(): void {
    if (this.form == null) {
      this.portionDisplayModes = {};
      return;
    }
    const modes: Record<string, ('input' | 'select')[]> = {};
    Object.keys(this.form.controls).forEach(userId => {
      const members = this.getCompanyProductUserMembersArray(userId);
      modes[userId] = [];
      members?.controls.forEach((control, rowIndex) => {
        const formGroup = control as FormGroup;
        modes[userId][rowIndex] = this.resolvePortionDisplayMode(userId, rowIndex, formGroup);
      });
    });
    this.portionDisplayModes = modes;
  }

  private rebuildMemberRows(): void {
    if (this.form == null) {
      this.memberRows.set([]);
      return;
    }

    const rows = Object.entries(this.form.controls).map(([userId, control]) => {
      const userGroup = control as FormGroup;
      const user = userGroup.get('user')?.value as User;
      const membersControl = userGroup.get('company_product_user_members') as FormArray | null;
      const members = (membersControl?.controls ?? []) as FormGroup[];

      return {
        userId,
        user,
        members,
      };
    });

    this.memberRows.set(rows);
  }
}

interface MemberRowSnapshot {
  company_product_user_member_role_id: number;
  portion_of_productive_work_day: number;
  expected_production_prcnt: number | null;
  start_date_key: string;
  end_date_key: string | null;
  end_date_api: Date | null;
}

export interface CompanyProductMembersDialogResult {
  saved: true;
  company_product_id: number;
  bulk: BulkDataStructureResponse<CompanyProductUserMember>;
  users_by_id: Record<number, User>;
}
